import type { RelationshipTimingComparison } from './relationshipTimingComparison.js';
import type { ChronologyResolution } from './chronology.js';
import { parseAstronomicalDate } from './chronology.js';
import { chronologyResolutionViewModel, type ChronologyResolutionViewModel } from './calculationContextPresentation.js';

const SCOPE_LABELS = Object.freeze({
  decadal: '大限',
  minor: '小限',
  yearly: '流年',
  monthly: '流月',
  daily: '流日',
  hourly: '流時',
} as const);

const SENSITIVITY_LABELS = Object.freeze({
  none: '無結構變化',
  low: '少量結構變化',
  medium: '多層結構變化',
  high: '廣泛結構變化',
} as const);

export interface RelationshipTimingComparisonLayerViewModel {
  scope: keyof typeof SCOPE_LABELS;
  scopeLabel: string;
  changed: boolean;
  changeCount: number;
  personAFocusCyclePalaceChanged: boolean;
  personBFocusCyclePalaceChanged: boolean;
  sharedFocusCyclePalaceStatusChanged: boolean;
  addedSharedMutagens: readonly string[];
  removedSharedMutagens: readonly string[];
  addedSharedFocusMovingStars: readonly string[];
  removedSharedFocusMovingStars: readonly string[];
}

export interface RelationshipTimingComparisonViewModel {
  model: RelationshipTimingComparison['model'];
  beforeLabel: string;
  afterLabel: string;
  structuralSensitivity: RelationshipTimingComparison['structuralSensitivity'];
  structuralSensitivityLabel: string;
  changedScopeCount: number;
  totalChangeCount: number;
  changedLayers: readonly RelationshipTimingComparisonLayerViewModel[];
  unchangedScopeCount: number;
  chronology?: Readonly<{ beforeTarget: ChronologyResolutionViewModel; afterTarget: ChronologyResolutionViewModel }>;
  disclosure: string;
}

function targetLabel(target: RelationshipTimingComparison['beforeTarget']): string {
  return `${target.solarDate}｜${target.lunarDate}｜時辰索引 ${target.timeIndex}`;
}

function assertTargetChronology(label: 'before' | 'after', target: RelationshipTimingComparison['beforeTarget'], resolution: ChronologyResolution): void {
  if (resolution.request.calendar !== 'solar') throw new Error(`Relationship timing comparison ${label} chronology must use solar target date`);
  const expected = parseAstronomicalDate(target.solarDate);
  const actual = resolution.request.date;
  if (actual.year !== expected.year || actual.month !== expected.month || actual.day !== expected.day) {
    throw new Error(`Relationship timing comparison ${label} chronology does not match target date`);
  }
}

export function relationshipTimingComparisonViewModel(
  comparison: RelationshipTimingComparison,
  chronology?: Readonly<{ beforeTarget: ChronologyResolution; afterTarget: ChronologyResolution }>,
): RelationshipTimingComparisonViewModel {
  const layers = comparison.differences.map((difference) => Object.freeze({
    scope: difference.scope,
    scopeLabel: SCOPE_LABELS[difference.scope],
    changed: difference.changeCount > 0,
    changeCount: difference.changeCount,
    personAFocusCyclePalaceChanged: difference.personAFocusCyclePalaceChanged,
    personBFocusCyclePalaceChanged: difference.personBFocusCyclePalaceChanged,
    sharedFocusCyclePalaceStatusChanged: difference.sharedFocusCyclePalaceStatusChanged,
    addedSharedMutagens: difference.addedSharedMutagens,
    removedSharedMutagens: difference.removedSharedMutagens,
    addedSharedFocusMovingStars: difference.addedSharedFocusMovingStars,
    removedSharedFocusMovingStars: difference.removedSharedFocusMovingStars,
  }));
  if (chronology) {
    assertTargetChronology('before', comparison.beforeTarget, chronology.beforeTarget);
    assertTargetChronology('after', comparison.afterTarget, chronology.afterTarget);
  }
  return Object.freeze({
    model: comparison.model,
    beforeLabel: targetLabel(comparison.beforeTarget),
    afterLabel: targetLabel(comparison.afterTarget),
    structuralSensitivity: comparison.structuralSensitivity,
    structuralSensitivityLabel: SENSITIVITY_LABELS[comparison.structuralSensitivity],
    changedScopeCount: comparison.changedScopeCount,
    totalChangeCount: comparison.totalChangeCount,
    changedLayers: Object.freeze(layers.filter((layer) => layer.changed)),
    unchangedScopeCount: layers.filter((layer) => !layer.changed).length,
    ...(chronology ? { chronology: Object.freeze({ beforeTarget: chronologyResolutionViewModel(chronology.beforeTarget), afterTarget: chronologyResolutionViewModel(chronology.afterTarget) }) } : {}),
    disclosure: comparison.disclosure,
  });
}

export interface RelationshipTimingComparisonPrintPacket {
  schemaVersion: '1';
  model: RelationshipTimingComparison['model'];
  beforeTarget: RelationshipTimingComparison['beforeTarget'];
  afterTarget: RelationshipTimingComparison['afterTarget'];
  structuralSensitivity: RelationshipTimingComparison['structuralSensitivity'];
  structuralSensitivityLabel: string;
  changedScopeCount: number;
  totalChangeCount: number;
  changedLayers: readonly RelationshipTimingComparisonLayerViewModel[];
  chronology?: Readonly<{ beforeTarget: ChronologyResolutionViewModel; afterTarget: ChronologyResolutionViewModel }>;
  disclosure: string;
}

export function buildRelationshipTimingComparisonPrintPacket(
  comparison: RelationshipTimingComparison,
  chronology?: Readonly<{ beforeTarget: ChronologyResolution; afterTarget: ChronologyResolution }>,
): RelationshipTimingComparisonPrintPacket {
  const view = relationshipTimingComparisonViewModel(comparison, chronology);
  return Object.freeze({
    schemaVersion: '1' as const,
    model: comparison.model,
    beforeTarget: comparison.beforeTarget,
    afterTarget: comparison.afterTarget,
    structuralSensitivity: comparison.structuralSensitivity,
    structuralSensitivityLabel: view.structuralSensitivityLabel,
    changedScopeCount: comparison.changedScopeCount,
    totalChangeCount: comparison.totalChangeCount,
    changedLayers: view.changedLayers,
    ...(view.chronology ? { chronology: view.chronology } : {}),
    disclosure: '此列印只比較兩個時間點的雙人運限結構差異，不提供較佳日期、吉凶排序、相合分數或事件機率。',
  });
}
